import { useState, useRef } from 'react'
import { PhotoIcon, XMarkIcon, PlusIcon } from '@heroicons/react/24/outline'

const MultipleImageUpload = ({
    images = [],
    onChange,
    placeholder = "Click to upload images",
    accept = "image/*",
    maxSize = 5 * 1024 * 1024, // 5MB
    maxImages = 10,
    className = "",
    disabled = false
}) => {
    const [isDragOver, setIsDragOver] = useState(false)
    const [error, setError] = useState('')
    const fileInputRef = useRef(null)

    const handleFileSelect = (files) => {
        setError('')

        if (images.length + files.length > maxImages) {
            setError(`Maximum ${maxImages} images allowed`)
            return
        }

        const newImages = []

        Array.from(files).forEach((file, index) => {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                setError(`File ${index + 1} is not a valid image`)
                return
            }

            // Validate file size
            if (file.size > maxSize) {
                setError(`File ${index + 1} size must be less than ${Math.round(maxSize / (1024 * 1024))}MB`)
                return
            }

            // Convert to base64
            const reader = new FileReader()
            reader.onload = (e) => {
                newImages.push({
                    url: e.target.result,
                    caption: '',
                    type: 'image'
                })

                if (newImages.length === files.length) {
                    onChange([...images, ...newImages])
                }
            }
            reader.readAsDataURL(file)
        })
    }

    const handleDrop = (e) => {
        e.preventDefault()
        setIsDragOver(false)

        const files = e.dataTransfer.files
        if (files.length > 0) {
            handleFileSelect(files)
        }
    }

    const handleDragOver = (e) => {
        e.preventDefault()
        setIsDragOver(true)
    }

    const handleDragLeave = (e) => {
        e.preventDefault()
        setIsDragOver(false)
    }

    const handleClick = () => {
        if (!disabled && images.length < maxImages) {
            fileInputRef.current?.click()
        }
    }

    const handleFileChange = (e) => {
        const files = e.target.files
        if (files.length > 0) {
            handleFileSelect(files)
        }
    }

    const handleRemove = (index) => {
        const newImages = images.filter((_, i) => i !== index)
        onChange(newImages)
    }

    const handleCaptionChange = (index, caption) => {
        const newImages = [...images]
        newImages[index].caption = caption
        onChange(newImages)
    }

    const handleTypeChange = (index, type) => {
        const newImages = [...images]
        newImages[index].type = type
        onChange(newImages)
    }

    return (
        <div className={`space-y-4 ${className}`}>
            {/* Upload Area */}
            {images.length < maxImages && (
                <div
                    onClick={handleClick}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    className={`
                        relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
                        ${isDragOver ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-gray-400'}
                        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={accept}
                        multiple
                        onChange={handleFileChange}
                        className="hidden"
                        disabled={disabled}
                    />

                    <div className="space-y-2">
                        <PlusIcon className="w-8 h-8 text-gray-400 mx-auto" />
                        <div className="text-sm text-gray-600">
                            {placeholder}
                        </div>
                        <div className="text-xs text-gray-500">
                            PNG, JPG, GIF up to {Math.round(maxSize / (1024 * 1024))}MB each
                        </div>
                        <div className="text-xs text-gray-500">
                            {images.length}/{maxImages} images
                        </div>
                    </div>
                </div>
            )}

            {error && (
                <p className="text-sm text-red-600">{error}</p>
            )}

            {/* Image Grid */}
            {images.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {images.map((image, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                            <div className="relative mb-3">
                                <img
                                    src={image.url}
                                    alt={`Upload ${index + 1}`}
                                    className="w-full h-32 object-cover rounded-lg"
                                />
                                {!disabled && (
                                    <button
                                        onClick={() => handleRemove(index)}
                                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                                    >
                                        <XMarkIcon className="w-4 h-4" />
                                    </button>
                                )}
                            </div>

                            <div className="space-y-2">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Type
                                    </label>
                                    <select
                                        value={image.type}
                                        onChange={(e) => handleTypeChange(index, e.target.value)}
                                        className="w-full text-xs border border-gray-300 rounded px-2 py-1"
                                        disabled={disabled}
                                    >
                                        <option value="image">Image</option>
                                        <option value="video">Video</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Caption
                                    </label>
                                    <input
                                        type="text"
                                        value={image.caption}
                                        onChange={(e) => handleCaptionChange(index, e.target.value)}
                                        className="w-full text-xs border border-gray-300 rounded px-2 py-1"
                                        placeholder="Enter caption"
                                        disabled={disabled}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

export default MultipleImageUpload
